/*
 * ノベルティ機能：カート内容に応じて `novelty` メタオブジェクトの条件を判定し、
 * 対象のノベルティ商品をカートへ自動追加・自動削除・数量調整する。
 * 設定は snippets/custom-cart-novelty.liquid が出力する #novelty-cart-config の
 * JSONから読み込む。カート内容そのものは常に /cart.js から都度取得し、
 * ページ読み込み時のスナップショットには依存しない（このファイル自体はLiquidに依存しない）。
 *
 * チェックアウトボタン（Enterキーでのフォーム送信を含む）は、クリック/送信の
 * 「瞬間」に必ず最新状態で再判定してからでないと通過させない。数量の+/-操作は
 * 画面遷移なしの部分更新のため、ページ読み込み時の1回だけの判定では
 * 「しきい値を超えた/下回ったのに検知できない」ケースが発生し得るため。
 *
 * 失敗時は購入手続きを止めない（フェイルオープン）：
 *  ・Storefront API / カート情報の取得失敗 → 判定をスキップ
 *  ・ノベルティの追加・削除の失敗（ノベルティ在庫切れ等）→ そのまま通す
 * 失敗はコンソールに [novelty] で出力し、GTM（dataLayer）へ novelty_error イベントを送信する。
 */
(() => {
  const CONFIG_ELEMENT_ID = 'novelty-cart-config';
  const STOREFRONT_API_VERSION = '2026-04';
  const METAOBJECT_TYPE = 'novelty';
  const NOVELTY_PRODUCT_TYPE = 'ノベルティ';
  const EXCLUDED_FROM_TARGET_PRODUCT_TYPES = [NOVELTY_PRODUCT_TYPE, '購入特典'];

  // theme_GOODS(参考サイト)のノベルティ機能で使われているアラート文言に合わせる。
  const NOVELTY_MESSAGES = {
    endedInCart: '付与期間外のノベルティがカートに残っています',
    noRegularProducts: 'ノベルティ対象商品を削除したため、カートを調整しました',
    conditionNotMet: 'ノベルティの条件を満たさなくなったため、カートから削除しました',
    adjusted: 'ノベルティの適用数が変更されたため、カートを調整しました',
  };

  function showNoveltyAlerts(messages) {
    if (!messages || !messages.length) return;
    // alert()がブロックされる/例外を投げる環境でも、後続のカート更新・
    // リロード処理が止まらないようにする（通知はあくまで補助情報のため）。
    try {
      alert([...new Set(messages)].join('\n\n'));
    } catch (error) {
      console.error('ノベルティ通知の表示に失敗しました:', error);
    }
  }

  const NOVELTY_ALERT_STORAGE_KEY = 'novelty_pending_alert';

  // リロードを伴う場合は、alert()を先に出すと「画面はまだ古いまま」の状態で
  // ユーザーに通知が割り込んでしまう。カート更新（サーバー側の削除）は既に
  // 完了しているので、先にリロードして正しい画面を見せてから、
  // リロード後のページ読み込み時に通知を表示する。
  function queueNoveltyAlerts(messages) {
    if (!messages || !messages.length) return;
    try {
      const unique = [...new Set(messages)];
      sessionStorage.setItem(NOVELTY_ALERT_STORAGE_KEY, JSON.stringify(unique));
      console.debug('[novelty] queued alerts for after reload:', unique);
    } catch (error) {
      console.error('ノベルティ通知の保存に失敗しました:', error);
    }
  }

  function flushPendingNoveltyAlerts() {
    try {
      const raw = sessionStorage.getItem(NOVELTY_ALERT_STORAGE_KEY);
      console.debug('[novelty] checking for pending alerts on load:', raw);
      if (!raw) return;
      sessionStorage.removeItem(NOVELTY_ALERT_STORAGE_KEY);
      showNoveltyAlerts(JSON.parse(raw));
    } catch (error) {
      console.error('保留していたノベルティ通知の表示に失敗しました:', error);
    }
  }

  // 失敗を GTM の dataLayer へ送信する。
  //   event: novelty_error
  //   novelty_error_type: fetch_failed（API/カート取得失敗）| cart_update_failed（削除・数量調整・クリア失敗）| cart_add_failed（追加失敗）
  //   novelty_error_message: エラー内容（先頭150文字）
  // 同じ種類のエラーは1ページにつき1回だけ送信する。
  const reportedErrorTypes = new Set();

  function reportNoveltyError(type, error) {
    const message = String((error && error.message) || error || '').slice(0, 150);
    console.error(`[novelty] ${type}:`, error);

    if (reportedErrorTypes.has(type)) return;
    reportedErrorTypes.add(type);

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'novelty_error',
        novelty_error_type: type,
        novelty_error_message: message,
      });
    } catch (pushError) {
      console.error('[novelty] dataLayer への送信に失敗しました:', pushError);
    }
  }

  function readConfig() {
    const el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('Novelty config の読み込みに失敗しました:', e);
      return null;
    }
  }

  const METAOBJECT_QUERY = `
    query getMetaobjects($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        edges {
          node {
            handle
            type
            fields {
              key
              value
              type
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const PRODUCT_COLLECTIONS_QUERY = `
    query getProductCollections($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          collections(first: 50) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  `;

  const gidToId = (gid) => {
    if (!gid || typeof gid !== 'string') return null;
    const parts = gid.split('/');
    return Number(parts[parts.length - 1]);
  };

  // window.__noveltyDebug はページ遷移で消えてしまうため、localStorageも見て
  // ページをまたいでもデバッグログが有効なままになるようにする。
  const isNoveltyDebugEnabled = () => {
    if (window.__noveltyDebug === true) return true;
    try {
      return window.localStorage.getItem('noveltyDebug') === '1';
    } catch (e) {
      return false;
    }
  };

  // 参照系フィールドは管理画面側の設定次第で単一参照(collection_reference)にも
  // リスト参照(list.collection_reference)にもなり得る。value のJSON構造が変わるため
  // field.type を見て分岐しないと、リスト型の場合に value をGIDとして直接パースしてしまい
  // (例: '["gid://shopify/Collection/123"]' の末尾 "]" までNumber化されNaNになる)、
  // 常にコレクション判定が成立しなくなる。
  const parseReferenceId = (field) => {
    if (!field) return null;
    if (typeof field.type === 'string' && field.type.startsWith('list.')) {
      try {
        const parsed = JSON.parse(field.value);
        return Array.isArray(parsed) && parsed.length ? gidToId(parsed[0]) : null;
      } catch (e) {
        return null;
      }
    }
    return gidToId(field.value);
  };

  const parseBool = (value) => value === true || value === 'true' || value === '1';
  const parseNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const toFieldMap = (node) => {
    const map = {};
    (node?.fields || []).forEach((field) => {
      map[field.key] = field;
    });
    return map;
  };

  function getPrice(fieldMap) {
    try {
      const noveltyPrice = JSON.parse(fieldMap.novelty_price?.value);
      if (noveltyPrice?.amount) {
        return parseNumber(noveltyPrice.amount, 1) * 100;
      }
      return Number.MAX_SAFE_INTEGER;
    } catch (e) {
      return Number.MAX_SAFE_INTEGER;
    }
  }

  function isWithinPeriod(startDate, endDate) {
    const nowTs = Date.now();
    const startTs = startDate ? Date.parse(startDate) : null;
    const endTs = endDate ? Date.parse(endDate) : null;

    if (Number.isFinite(startTs) && nowTs < startTs) return false;
    if (Number.isFinite(endTs) && nowTs > endTs) return false;
    return true;
  }

  async function storefrontQuery(shopDomain, storefrontAccessToken, query, variables) {
    const endpoint = `https://${shopDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) {
      throw new Error(`Storefront API request failed: ${response.status}`);
    }
    const result = await response.json();
    if (result.errors) {
      throw new Error(`Storefront API GraphQL errors: ${JSON.stringify(result.errors)}`);
    }
    return result.data;
  }

  async function fetchMetaobjects(shopDomain, storefrontAccessToken, type = METAOBJECT_TYPE) {
    const limit = 250;
    let hasNextPage = true;
    let after = null;
    const allNodes = [];

    while (hasNextPage) {
      const data = await storefrontQuery(shopDomain, storefrontAccessToken, METAOBJECT_QUERY, {
        type,
        first: limit,
        after,
      });
      const metaobjects = data?.metaobjects;
      if (!metaobjects) break;

      allNodes.push(...(metaobjects.edges || []).map((edge) => edge.node));
      hasNextPage = metaobjects.pageInfo?.hasNextPage;
      after = metaobjects.pageInfo?.endCursor;
    }

    return allNodes;
  }

  async function fetchProductCollections(shopDomain, storefrontAccessToken, productIds) {
    if (!productIds.length) return {};
    const ids = productIds.map((id) => `gid://shopify/Product/${id}`);
    const data = await storefrontQuery(shopDomain, storefrontAccessToken, PRODUCT_COLLECTIONS_QUERY, { ids });
    if (isNoveltyDebugEnabled()) {
      console.debug('[novelty] raw getProductCollections response', { ids, data });
    }
    const map = {};
    (data?.nodes || []).forEach((node) => {
      if (!node) return;
      const productId = gidToId(node.id);
      map[productId] = (node.collections?.edges || []).map((edge) => gidToId(edge.node.id)).filter(Boolean);
    });
    return map;
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      return response.json();
    });
  }

  function cartFetch(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || `Cart request failed: ${response.status}`);
      }
      return response.json();
    });
  }

  function computeRequirement(node, cartItems) {
    const fieldMap = toFieldMap(node);
    const noveltyVariantId = gidToId(fieldMap.novelty_product_variant?.value);

    let targetVariants = [];
    if (fieldMap.novelty_target_variants?.value) {
      try {
        const parsed = JSON.parse(fieldMap.novelty_target_variants.value);
        targetVariants = Array.isArray(parsed) ? parsed.map(gidToId).filter(Boolean) : [];
      } catch (e) {
        targetVariants = [];
      }
    }

    const novelty = {
      variantId: noveltyVariantId,
      collectionId: parseReferenceId(fieldMap.novelty_collection),
      price: getPrice(fieldMap),
      maxQuantity: parseNumber(fieldMap.novelty_max_quantity?.value, 0),
      condition: fieldMap.novelty_condition?.value || '',
      applyForEach: parseBool(fieldMap.apply_for_each_condition?.value),
      noveltyQuantity: parseNumber(fieldMap.novelty_quantity?.value, 1) > 0 ? parseNumber(fieldMap.novelty_quantity?.value, 1) : 1,
      startDate: fieldMap.novelty_start_date?.value || '',
      endDate: fieldMap.novelty_end_date?.value || '',
      target: fieldMap.novelty_target?.value || '',
      applyToAllTargetProducts: parseBool(fieldMap.apply_to_all_target_products?.value),
      applyToEntireCart: parseBool(fieldMap.apply_to_entire_cart?.value),
      targetVariants,
    };

    if (!novelty.variantId) return null;

    const result = { variantId: novelty.variantId, add: false, inCart: false, currentQty: 0, requiredQty: 0 };

    if (!isWithinPeriod(novelty.startDate, novelty.endDate)) {
      return result;
    }

    const nonNoveltyItems = cartItems.filter((item) => !EXCLUDED_FROM_TARGET_PRODUCT_TYPES.includes(item.productType));

    const targetVariantSet = new Set(novelty.targetVariants || []);

    cartItems.forEach((item) => {
      if (item.variantId === novelty.variantId) {
        result.inCart = true;
        result.currentQty += item.quantity;
      }
    });

    // ゲート判定（対象商品/対象コレクションが最低1個あるか）は、集計範囲を
    // カート全体に広げるかどうかに関わらず、常に本来の対象範囲に対して行う。
    const isTargetMatch = (item) => {
      if (novelty.target === '商品') {
        return targetVariantSet.size > 0 && targetVariantSet.has(item.variantId);
      }
      if (novelty.target === 'コレクション' && novelty.collectionId) {
        return (item.collections || []).includes(novelty.collectionId);
      }
      return true;
    };

    const foundTargetVariants = new Set();
    let collectionPresent = false;
    nonNoveltyItems.forEach((item) => {
      if (!isTargetMatch(item)) return;
      if (novelty.target === '商品') foundTargetVariants.add(item.variantId);
      if (novelty.target === 'コレクション') collectionPresent = true;
    });

    let presentSetCount = 0;
    if (novelty.target === '商品' && novelty.applyToAllTargetProducts && targetVariantSet.size > 0) {
      presentSetCount = Infinity;
      targetVariantSet.forEach((vid) => {
        const qtySumForVid = nonNoveltyItems.reduce((sum, item) => (item.variantId === vid ? sum + item.quantity : sum), 0);
        if (qtySumForVid < presentSetCount) presentSetCount = qtySumForVid;
      });
      if (presentSetCount === Infinity) presentSetCount = 0;
    }

    // 集計範囲（金額・数量をどの商品から合計するか）：
    // apply_to_entire_cart が true ならカート全体、false なら従来通り対象範囲内のみ。
    let cartTotal = 0;
    let targetQuantitySum = 0;
    nonNoveltyItems.forEach((item) => {
      const isInSum = novelty.applyToEntireCart || isTargetMatch(item);
      if (isInSum) {
        cartTotal += item.finalLinePrice;
        targetQuantitySum += item.quantity;
      }
    });

    let requiredQty = 0;
    if (novelty.condition === '数量条件') {
      if (!novelty.applyToEntireCart && novelty.target === '商品' && novelty.applyToAllTargetProducts) {
        targetQuantitySum = presentSetCount;
      }
      if (novelty.applyForEach) {
        requiredQty = Math.floor(targetQuantitySum / novelty.noveltyQuantity);
      } else if (targetQuantitySum >= novelty.noveltyQuantity) {
        requiredQty = 1;
      }
    } else {
      if (novelty.applyForEach) {
        requiredQty = Math.floor(cartTotal / novelty.price);
      } else if (cartTotal >= novelty.price) {
        requiredQty = 1;
      }
      if (!novelty.applyToEntireCart && novelty.target === '商品' && novelty.applyToAllTargetProducts && presentSetCount === 0) {
        requiredQty = 0;
      }
    }

    // ゲート適用：対象商品/対象コレクションが実際にカート内に存在しなければ、
    // カート全体の合計がどれだけ大きくても付与しない。
    if (novelty.target === '商品') {
      const productPresenceOk = novelty.applyToAllTargetProducts ? presentSetCount > 0 : foundTargetVariants.size > 0;
      if (!productPresenceOk) requiredQty = 0;
    } else if (novelty.target === 'コレクション' && novelty.collectionId) {
      if (!collectionPresent) requiredQty = 0;
    }

    if (isNoveltyDebugEnabled() && novelty.target === 'コレクション') {
      console.debug('[novelty] collection target check', {
        node: node.handle,
        collectionId: novelty.collectionId,
        collectionPresent,
        requiredQty,
        itemCollections: cartItems.map((i) => ({ variantId: i.variantId, collections: i.collections })),
      });
    }

    if (novelty.maxQuantity > 0 && requiredQty > novelty.maxQuantity) {
      requiredQty = novelty.maxQuantity;
    }

    result.requiredQty = requiredQty;
    result.add = requiredQty > 0;
    return result;
  }

  function shouldClearCart(cartItems) {
    const hasRegularProduct = cartItems.some((item) => !EXCLUDED_FROM_TARGET_PRODUCT_TYPES.includes(item.productType));
    return !hasRegularProduct && cartItems.length > 0;
  }

  function hasMismatch(results) {
    return results.some((r) => (r.add ? r.currentQty !== r.requiredQty : r.inCart));
  }

  // カート・メタオブジェクト・(必要なら)商品のコレクション所属を毎回すべて取得し直す。
  // ページ読み込み時のスナップショットには一切依存しない。
  async function evaluate(config) {
    const rawCart = await fetchCart();
    let cartItems = (rawCart.items || []).map((item) => ({
      key: item.key,
      variantId: item.variant_id,
      productId: item.product_id,
      quantity: item.quantity,
      finalLinePrice: item.final_line_price,
      productType: item.product_type,
      productTitle: item.product_title,
      collections: [],
    }));

    const metaobjectNodes = await fetchMetaobjects(config.shopDomain, config.storefrontAccessToken);
    if (!metaobjectNodes.length) return { cartItems, results: [], metaobjectNodes };

    const needsCollections = metaobjectNodes.some((node) => toFieldMap(node).novelty_target?.value === 'コレクション');
    if (needsCollections) {
      const productIds = [...new Set(cartItems.map((item) => item.productId))];
      const collectionsByProductId = await fetchProductCollections(
        config.shopDomain,
        config.storefrontAccessToken,
        productIds
      ).catch((error) => {
        // コレクション所属が取れなくても他の条件の判定は続ける
        reportNoveltyError('fetch_failed', error);
        return {};
      });
      cartItems = cartItems.map((item) => ({ ...item, collections: collectionsByProductId[item.productId] || [] }));
      if (isNoveltyDebugEnabled()) {
        console.debug('[novelty] fetched collections by product', collectionsByProductId, 'cartItems', cartItems);
      }
    }

    const results = metaobjectNodes.map((node) => computeRequirement(node, cartItems)).filter(Boolean);
    return { cartItems, results, metaobjectNodes };
  }

  async function reconcile(cartItems, results, routes) {
    const updates = {};
    const itemsToAdd = [];
    const alertMessages = [];

    results.forEach((result) => {
      if (!result.add && result.inCart) {
        const item = cartItems.find((i) => i.variantId === result.variantId);
        if (item) {
          updates[item.key] = 0;
          alertMessages.push(NOVELTY_MESSAGES.conditionNotMet);
        }
      } else if (result.add && result.currentQty < result.requiredQty) {
        itemsToAdd.push({ id: result.variantId, quantity: result.requiredQty - result.currentQty });
      } else if (result.add && result.currentQty > result.requiredQty) {
        let toRemove = result.currentQty - result.requiredQty;
        let removedAny = false;
        cartItems
          .filter((item) => item.variantId === result.variantId)
          .forEach((item) => {
            if (toRemove <= 0) return;
            if (item.quantity <= toRemove) {
              updates[item.key] = 0;
              toRemove -= item.quantity;
            } else {
              updates[item.key] = item.quantity - toRemove;
              toRemove = 0;
            }
            removedAny = true;
          });
        if (removedAny) alertMessages.push(NOVELTY_MESSAGES.adjusted);
      }
    });

    let changed = false;

    if (Object.keys(updates).length > 0) {
      try {
        await cartFetch(routes.cartUpdateUrl, { updates });
        changed = true;
      } catch (error) {
        reportNoveltyError('cart_update_failed', error);
      }
    }

    if (itemsToAdd.length > 0) {
      try {
        await cartFetch(routes.cartAddUrl, { items: itemsToAdd });
        changed = true;
      } catch (error) {
        // ノベルティの在庫切れ等。購入手続きは止めない
        reportNoveltyError('cart_add_failed', error);
      }
    }

    // 失敗分の通知は出さない（成功分のみ通知）
    return { changed, alertMessages: changed ? alertMessages : [] };
  }

  // リロード開始後は以降の判定・カート操作を行わない
  let isReloading = false;
  function reloadPage() {
    isReloading = true;
    window.location.reload();
  }

  // 判定〜是正は同時に走らせない（ページ読み込み時・数量変更時・購入手続き時が重なっても二重追加しない）
  let cycleChain = Promise.resolve();
  function queueCycle(config) {
    const next = cycleChain.then(() => (isReloading ? 'reloaded' : runCycle(config)));
    cycleChain = next.catch(() => {});
    return next;
  }

  // 1サイクル分の判定〜是正を行う。戻り値：
  //  'reloaded' … カートを更新してリロードした（このサイクルの呼び出し元は以降何もしない）
  //  'ok'       … チェックアウトを進めてよい（取得・カート更新の失敗時も含む。失敗は GTM へ送信）
  async function runCycle(config) {
    let evaluation;
    try {
      evaluation = await evaluate(config);
    } catch (error) {
      reportNoveltyError('fetch_failed', error);
      return 'ok';
    }

    const { cartItems, results, metaobjectNodes } = evaluation;
    if (!metaobjectNodes || !metaobjectNodes.length) return 'ok';

    const validIds = new Set(results.map((r) => r.variantId).filter(Boolean));
    const expiredIds = new Set(
      metaobjectNodes
        .map((node) => {
          const fieldMap = toFieldMap(node);
          return !isWithinPeriod(fieldMap.novelty_start_date?.value, fieldMap.novelty_end_date?.value)
            ? gidToId(fieldMap.novelty_product_variant?.value)
            : null;
        })
        .filter(Boolean)
    );

    const disabledKeys = cartItems
      .filter((item) => item.productType === NOVELTY_PRODUCT_TYPE)
      .filter((item) => !validIds.has(item.variantId) || expiredIds.has(item.variantId))
      .map((item) => item.key);

    if (disabledKeys.length > 0) {
      const updates = {};
      disabledKeys.forEach((key) => {
        updates[key] = 0;
      });
      try {
        await cartFetch(config.routes.cartUpdateUrl, { updates });
        queueNoveltyAlerts([NOVELTY_MESSAGES.endedInCart]);
        reloadPage();
        return 'reloaded';
      } catch (error) {
        reportNoveltyError('cart_update_failed', error);
        return 'ok';
      }
    }

    if (shouldClearCart(cartItems)) {
      try {
        await cartFetch(config.routes.cartClearUrl, {});
        queueNoveltyAlerts([NOVELTY_MESSAGES.noRegularProducts]);
        reloadPage();
        return 'reloaded';
      } catch (error) {
        reportNoveltyError('cart_update_failed', error);
        return 'ok';
      }
    }

    if (!hasMismatch(results)) return 'ok';

    const { changed, alertMessages } = await reconcile(cartItems, results, config.routes);
    if (changed) {
      // カート更新（削除・調整・追加）は既に完了しているので、先にリロードして
      // 正しい画面を見せてから、次のページ読み込み時に通知する。
      queueNoveltyAlerts(alertMessages);
      reloadPage();
      return 'reloaded';
    }
    // 是正に失敗した場合も購入手続きは止めない（失敗は reportNoveltyError で記録済み）
    return 'ok';
  }

  function setupCheckoutGuard(config) {
    const checkoutButton = document.getElementById('checkout');
    // "checkout" というidは、ゲスト購入不可商品の分岐でログイン誘導の<a>要素にも
    // 使われている。このケースは実際の購入手続きではないため対象外にする。
    if (!checkoutButton || checkoutButton.tagName !== 'BUTTON') return;

    const form = checkoutButton.form || checkoutButton.closest('form') || document.querySelector('form[action="/cart"]');

    let bypassClick = false;
    let bypassSubmit = false;
    let pendingCheck = null;

    // クリック/送信の「瞬間」に必ず最新のカート状態で再検証する。
    // 既存の同意/販売期間/会場受け取りスロットの統合判定とは独立に動作し、
    // disabled属性やスタイルは操作しない（このボタンを直接disabledにするのは
    // 検証中の二重送信防止のみに限定し、完了後は必ず元の状態に戻す）。
    async function guard(event, isSubmit) {
      if (isSubmit ? bypassSubmit : bypassClick) {
        if (isSubmit) {
          bypassSubmit = false;
        } else {
          bypassClick = false;
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!pendingCheck) {
        const wasDisabled = checkoutButton.disabled;
        checkoutButton.disabled = true;
        pendingCheck = queueCycle(config).finally(() => {
          pendingCheck = null;
          checkoutButton.disabled = wasDisabled;
        });
      }

      const outcome = await pendingCheck;
      if (outcome === 'reloaded') return;

      if (isSubmit) {
        bypassSubmit = true;
        // submitter を渡さないと checkout ボタンの name/value（checkout パラメータ）が送信されない
        if (form) form.requestSubmit(checkoutButton);
      } else {
        // クリックを許可すると、ボタンのデフォルト動作としてフォームのsubmitイベントも
        // 発火するため、そちらの再検証は不要（二重チェックを避ける）
        bypassClick = true;
        bypassSubmit = true;
        checkoutButton.click();
      }
    }

    checkoutButton.addEventListener('click', (event) => guard(event, false), true);
    if (form) {
      form.addEventListener(
        'submit',
        (event) => {
          // 購入手続き以外の送信（カート更新ボタン等）は対象外
          if (event.submitter && event.submitter !== checkoutButton) return;
          guard(event, true);
        },
        true
      );
    }

    // ページ読み込み時にも先回りで是正しておく。guard()による
    // クリック時再検証こそが最終防衛ラインであり、ここでの実行はUX向上のため。
    queueCycle(config);

    // カートページで数量変更・削除を行うと、画面遷移なしにcart-itemsが
    // AJAX更新される（PUB_SUB_EVENTS.cartUpdateが発火）。これを検知して都度
    // 再判定しないと、対象商品を消してもページを再読み込みするまでノベルティが
    // カートに残り続けてしまう。runCycleが「対象商品なし＝ノベルティのみ」の
    // カートを検知した場合はcartClearUrlで自動的に空にしてリロードする
    // （shouldClearCart）ため、ここでの再判定がそのまま自動削除につながる。
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => queueCycle(config));
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    // 前回のリロード前に保留した通知があれば、まずそれを表示する
    // （カートの表示はこの時点で既に最新状態になっている）。
    flushPendingNoveltyAlerts();

    const config = readConfig();
    if (!config) return;

    if (!config.storefrontAccessToken) {
      console.warn('Novelty Storefront API token is not set.');
      return;
    }

    setupCheckoutGuard(config);
  });
})();
